/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { gql, useMutation } from "@apollo/client";
import { FileUploadManifest, FileUploadResponse } from "Api";
import { AxiosContext } from "../../Utils/AuthContext";
import { Endpoints } from "../../Utils/Common";
import { createUUID } from "../../Utils/Types";
import { loggedNotify, Component } from "../Notify";
import removeFilesGql from "../../Api/Gql/RemoveFiles";
import type { RemoveFileFn, UploadFileFn } from "./UploadModal";

interface ParsedPdfFile {
  id: string;
  name: string;
}

/**
 * Provides the Dexter upload/remove operations the UploadModal needs.
 * Kept out of the modal so it doesn't affect the story.
 *
 * The Files page currently uses this.  Eventually, the transform engine canvas
 * can as well.
 *
 * Upload is a multipart POST to /upload; the client-generated file id is what
 * the server stores, so it is also the id used to remove the file later.
 */
export function useDexterFileUpload(): {
  uploadFile: UploadFileFn;
  removeFile: RemoveFileFn;
} {
  const axiosContext = React.useContext(AxiosContext);
  const [removeFilesMutation] = useMutation(gql(removeFilesGql));

  const uploadFile: UploadFileFn = React.useCallback(
    async (file, { onProgress, signal }) => {
      const fileId = createUUID();
      const manifest: FileUploadManifest = {
        fileInfo: [{ id: fileId, name: file.name }],
      };

      const formData = new FormData();
      formData.append("fileInfo", JSON.stringify(manifest));
      // "0" is just the file part's field name (placeholder); the server matches
      // the file to its manifest entry by filename, not by this name.
      formData.append("0", file, file.name);

      const response = await axiosContext.post(Endpoints.UploadFile, formData, {
        signal,
        onUploadProgress: (event) => {
          if (event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      });

      const { successfulUploads, duplicateUploads, failedUploads } =
        response.data as FileUploadResponse;

      const failure = failedUploads?.find((f) => f.fileName === file.name);
      if (failure) {
        throw new Error(failure.uploadError || "Upload failed.");
      }

      const succeeded = [
        ...(successfulUploads ?? []),
        ...(duplicateUploads ?? []),
      ].includes(file.name);
      if (!succeeded) {
        throw new Error("Upload failed.");
      }

      // A PDF was parsed into its constituent files server-side; show those
      // generated files in place of the PDF instead of the PDF itself.
      if (file.name.toLowerCase().endsWith(".pdf")) {
        const parsed = await axiosContext.get(Endpoints.GetParsedPdfFiles, {
          params: { pdfId: fileId },
        });
        const parsedFiles = (parsed.data as ParsedPdfFile[]) ?? [];

        if (parsedFiles.length === 0) {
          // Nothing could be extracted. Clean up the stored-but-hidden PDF and
          // show the failure as a notification; the row then shows an error.
          loggedNotify.negative(
            axiosContext,
            Component.FILES,
            `No files could be extracted from ${file.name}.`
          );
          await removeFilesMutation({
            variables: { fileIds: [fileId] },
          }).catch(() => {
            // Best-effort cleanup of the hidden PDF.
          });
          throw new Error(`No files could be extracted from ${file.name}.`);
        }

        return parsedFiles.map((parsedFile) => ({
          id: parsedFile.id,
          name: parsedFile.name,
          pdfParentId: fileId,
        }));
      }

      return [{ id: fileId, name: file.name }];
    },
    [axiosContext, removeFilesMutation]
  );

  const removeFile: RemoveFileFn = React.useCallback(
    async (fileId) => {
      await removeFilesMutation({ variables: { fileIds: [fileId] } });
    },
    [removeFilesMutation]
  );

  return { uploadFile, removeFile };
}
